/**
 * devolucionRefundCalculator.ts — Valor real a reembolsar por línea de venta.
 *
 * Aplica descuentos por ítem y global (sin propina/servicio).
 * Usa snapshot persistido en la venta cuando existe; si no, infiere proporcional.
 */

import type { VentaProducto } from "../types/caja.types";

export interface VentaDescuentosSnapshot {
  totalDescuentoItems: number;
  descuentoGlobal: number;
  lineAdjustments?: Array<{
    nombre: string;
    descuentoTotalLinea: number;
  }>;
  /** Detalle por línea para devoluciones exactas (ventas nuevas). */
  lineDetails?: VentaLineRefundDetail[];
}

export interface VentaLineRefundDetail {
  productoId: string;
  variantKey?: string;
  modifierKey?: string;
  nombre: string;
  bruto: number;
  netoFinal: number;
  cantidad: number;
  precioUnitarioFinal: number;
  precioUnitarioOriginal?: number;
}

export interface RefundLineInput {
  productoId: string;
  variantKey?: string;
  modifierKey?: string;
  nombre: string;
  cantidadVendida: number;
  precioUnitarioCatalogo: number;
  peso?: number;
  precioPorKilo?: number;
}

export interface RefundLineResult {
  productoId: string;
  nombre: string;
  cantidad: number;
  /** Precio unitario efectivamente pagado (post descuentos, sin propina). */
  precioUnitario: number;
  precioCatalogo: number;
  subtotalLinea: number;
}

export interface RefundCalculation {
  items: RefundLineInput[];
  linePricing: Map<string, { unitEffective: number; unitCatalog: number }>;
  selections: Record<string, number>;
}

const clampMoney = (value: number): number => {
  if (isNaN(value) || value < 0) return 0;
  return Math.round(value);
};

export const ventaProductoKey = (p: {
  productoId: string;
  variantKey?: string;
  modifierKey?: string;
}): string => `${p.productoId}#${p.variantKey ?? ""}#${p.modifierKey ?? ""}`;

const lineBruto = (line: RefundLineInput): number => {
  const price = line.precioUnitarioCatalogo ?? 0;
  const qty = Math.max(1, line.cantidadVendida ?? 1);
  if (line.peso && line.peso > 0 && (line.precioPorKilo ?? 0) > 0) {
    return clampMoney((line.precioPorKilo ?? 0) * line.peso);
  }
  return clampMoney(price * qty);
};

/** Mapa producto → precio unitario efectivo pagado (sin propina). */
export function buildVentaLineRefundPricing(
  items: RefundLineInput[],
  descuentos?: VentaDescuentosSnapshot | null,
): Map<string, { unitEffective: number; unitCatalog: number }> {
  const map = new Map<string, { unitEffective: number; unitCatalog: number }>();

  const detailsByKey = new Map<string, VentaLineRefundDetail>();
  for (const d of descuentos?.lineDetails ?? []) {
    detailsByKey.set(
      ventaProductoKey({
        productoId: d.productoId,
        variantKey: d.variantKey,
        modifierKey: d.modifierKey,
      }),
      d,
    );
  }

  if (detailsByKey.size > 0) {
    for (const item of items) {
      const key = ventaProductoKey(item);
      const detail = detailsByKey.get(key);
      const unitCatalog = clampMoney(item.precioUnitarioCatalogo ?? 0);
      if (detail && (detail.cantidad ?? 0) > 0) {
        map.set(key, {
          unitEffective: clampMoney(
            detail.precioUnitarioFinal ||
              (detail.netoFinal ?? 0) / (detail.cantidad ?? 1),
          ),
          unitCatalog:
            clampMoney(detail.precioUnitarioOriginal ?? unitCatalog) ||
            unitCatalog,
        });
      } else {
        map.set(key, { unitEffective: unitCatalog, unitCatalog });
      }
    }
    return map;
  }

  const brutos = items.map((item) => ({
    item,
    key: ventaProductoKey(item),
    bruto: lineBruto(item) || 0,
  }));
  const subtotalBruto = brutos.reduce((s, b) => s + b.bruto, 0);
  const totalDescuento =
    (descuentos?.totalDescuentoItems ?? 0) + (descuentos?.descuentoGlobal ?? 0);

  for (const { item, key, bruto } of brutos) {
    const qty = Math.max(1, item.cantidadVendida ?? 1);
    const unitCatalog = clampMoney(item.precioUnitarioCatalogo ?? 0);
    let neto = bruto;
    if (totalDescuento > 0 && subtotalBruto > 0) {
      const share = bruto / subtotalBruto;
      neto = clampMoney(bruto - share * totalDescuento);
    }
    map.set(key, {
      unitEffective: clampMoney(neto / qty),
      unitCatalog,
    });
  }

  return map;
}

/** Convierte productos persistidos en la venta al input del calculador. */
export function ventaProductosToRefundInputs(
  productos: VentaProducto[],
): RefundLineInput[] {
  return productos.map((p) => ({
    productoId: p.productoId,
    variantKey: p.variantKey,
    modifierKey: p.modifierKey,
    nombre: p.nombre,
    cantidadVendida: p.cantidad,
    precioUnitarioCatalogo: p.precioUnitario,
    peso: p.peso,
    precioPorKilo: p.precioPorKilo,
  }));
}

/** Calcula líneas de devolución con montos efectivos pagados. */
export function calculateRefundLineItems(
  items: RefundLineInput[],
  selections: Record<string, number>,
  descuentos?: VentaDescuentosSnapshot | null,
): RefundLineResult[] {
  const pricing = buildVentaLineRefundPricing(items, descuentos);

  return items
    .map((item) => {
      const key = ventaProductoKey(item);
      const qty = selections[key] ?? selections[item.productoId] ?? 0;
      if (qty <= 0) return null;
      const price = pricing.get(key) ?? {
        unitEffective: item.precioUnitarioCatalogo,
        unitCatalog: item.precioUnitarioCatalogo,
      };
      const subtotalLinea = clampMoney(price.unitEffective * qty);
      return {
        productoId: item.productoId,
        nombre: item.nombre,
        cantidad: qty,
        precioUnitario: price.unitEffective,
        precioCatalogo: price.unitCatalog,
        subtotalLinea,
      };
    })
    .filter((line): line is RefundLineResult => line !== null);
}

export const sumRefundLineItems = (lines: RefundLineResult[]): number =>
  lines.reduce((s, l) => s + l.subtotalLinea, 0);

/** Etiqueta de descuento aplicado vs catálogo (solo UI). */
export function formatRefundUnitLabel(
  unitEffective: number,
  unitCatalog: number,
): string {
  if (unitEffective >= unitCatalog) {
    return `$${unitEffective.toLocaleString("es-CO")}`;
  }
  return `$${unitEffective.toLocaleString("es-CO")} (pagó $${unitCatalog.toLocaleString("es-CO")})`;
}
